import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {of as observableOf,  Observable } from 'rxjs';
import {catchError} from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { ActivityFilter, IActivityLogs, IAttendeeDocumentPDFRequest, IAttendeeUpdateDocuments, IAttendeeUpdateGroups, IDocumentBadge, IPorscheExports } from './attendees-model';
import { AttendeesPage, IUpdateAttendee } from '@routes/events/models/attendeelist-model';
import { IDocumentPreview } from '../documents-service/documents-model';
import { SyncAttendeesToFd } from '../../legacy/attendees-service/attendees-model';

@Injectable()
export class AttendeesService {
    constructor(
        private http: HttpClient
    ) { }

    private static get URL(): string { return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`; }
    private static readonly ENDPOINT = 'events/{eventId}/attendees';

    public getActivityLogs(eventId: string, activityFilter: ActivityFilter): Observable<SuccessResponse<IActivityLogs> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IActivityLogs>>(AttendeesService.URL +
                AttendeesService.ENDPOINT.replace('{eventId}', eventId) +
                '/activities', activityFilter)
            .pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateAttendeesBatch(eventId: string, model: IAttendeeUpdateGroups): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL + AttendeesService.ENDPOINT.replace('{eventId}', eventId) + '/batchupdates', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateAttendee(eventId: string, model: IUpdateAttendee): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(AttendeesService.URL + AttendeesService.ENDPOINT.replace('{eventId}', eventId) + '/' + model.attendeeId, {groupIds: model.groupIds} ).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addAttendeeDocuments(eventId: string, model: IAttendeeUpdateDocuments): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(AttendeesService.URL + 'events/' + eventId + '/' + 'attendeedocuments', model ).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getPorscheExport(eventId: string, model: AttendeesPage): Observable<SuccessResponse<IPorscheExports> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(AttendeesService.URL + AttendeesService.ENDPOINT.replace('{eventId}', eventId) + '/porscheexports', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public removeAttendeeDocuments(eventId: string, model: IAttendeeUpdateDocuments): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const options = {
            body: model
        };
        return this.http
            .delete<SuccessResponse<boolean>>(AttendeesService.URL + 'events/' + eventId + '/' + 'attendeedocuments', options)
            .pipe(catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getAttendeeDocuments(eventId: string, attendeeId: string): Observable<SuccessResponse<IDocumentBadge> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IDocumentBadge>>(
                AttendeesService.URL + 'events/' + eventId + '/' + 'attendeedocuments' + '/' + attendeeId
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getAttendeeBadgeDocumentPDFPreview(attendeeDocumentId: string, eventId: string, body: IAttendeeDocumentPDFRequest) {
        return this.http
        .post<SuccessResponse<IDocumentPreview>>(
            AttendeesService.URL + 'events/' + eventId + '/' + 'attendeedocuments' + '/' + attendeeDocumentId, body
        ).pipe(
        catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getAttendeeSignatureDocumentPDFPreview(eventId: string, blobName: string, name: string) {
        return this.http
            .get(
                AttendeesService.URL + 'events/' + eventId + '/' + 'attendeedocuments/downloads/' + blobName,
                { responseType: 'blob', observe: 'response' }
            ).pipe(
                catchError(x => observableOf(new ErrorResponse(x?.error?.messages))))
            .subscribe((response: HttpResponse<Blob>) => {
                const urlObject = URL.createObjectURL(response.body);

                // Create an anchor element to "point" to it
                const anchor = document.createElement('a');
                anchor.href = urlObject;

                const filename = name + '.pdf';
                anchor.download = filename;

                // Simulate a click on our anchor element
                document.body.appendChild(anchor); // Append anchor to the body
                anchor.click();
                document.body.removeChild(anchor); // Remove the anchor from the body

                // Discard the object data
                URL.revokeObjectURL(urlObject);
            });
    }

    public syncAttendeesToFd(eventId: string, data: SyncAttendeesToFd): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(
                AttendeesService.URL + AttendeesService.ENDPOINT.replace('{eventId}', eventId) + '/synctofds', data
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

}
